import type { ReactNode } from "react";

import { ExpandIcon } from "@/components/appIcons";

export type CatalogFaqItem = {
  answer: ReactNode;
  id: string;
  question: ReactNode;
};

/**
 * One group of FAQ questions rendered as native `<details>` accordion items.
 * The wrapper uses `display: contents` so the items flow directly into the
 * two-column grid owned by `CatalogFaqAccordion`; the group title stays as a
 * screen-reader heading. Answers always stay in the DOM for search engines.
 */
export function CatalogFaqSection({ items, title }: { items: CatalogFaqItem[]; title: ReactNode }) {
  if (items.length === 0) return null;

  return (
    <div className="contents">
      <h3 className="sr-only">{title}</h3>
      {items.map((item) => (
        <CatalogFaqAccordionItem key={item.id} item={item} />
      ))}
    </div>
  );
}

function CatalogFaqAccordionItem({ item }: { item: CatalogFaqItem }) {
  return (
    <details data-catalog-faq-item="" className="group min-w-0 border-b border-border/60 py-4">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-sm text-base font-bold leading-snug text-foreground outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary [&::-webkit-details-marker]:hidden">
        <span className="min-w-0">{item.question}</span>
        <ExpandIcon
          aria-hidden="true"
          className="shrink-0 text-lg text-muted-foreground transition-transform group-open:rotate-180 motion-reduce:transition-none"
        />
      </summary>
      <div className="pt-2.5 text-[15px] font-medium leading-relaxed text-muted-foreground">
        {item.answer}
      </div>
    </details>
  );
}
